import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { DefaultLayout } from "../../../../layouts/Default";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { Main } from "../../../../themes/Default/components/Main";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { Slide, toast, ToastContainer } from "react-toastify";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";
import SummerNote from "../../../../packages/SummerNote/SummerNote";
import { Title } from "../../../../themes/Default/components/Title";
import { studentsManagement } from "../../../../hooks/api/StudentsManagement";

const GeneralGrade = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [formErrors, setFormErrors] = useState([]);
    const { reportId } = useParams();
    const { groupId } = useParams();
    const { studentId } = useParams();
    const [saving, setSaving] = useState(false);
    const [groupReportStudentId, setGroupReportStudentId] = useState(false);

    const { useViewStudentsMutation } = studentsManagement;
    const [viewStudent] = useViewStudentsMutation();
    const [studentFullName, setStudentFullName] = useState([]);

    const { useViewReportStudentMutation, useAddReportStudentMutation, useEditReportStudentMutation, useViewGroupMutation} = groupsManagement;
    const [viewReportStudent] = useViewReportStudentMutation();
    const [addReportStudent] = useAddReportStudentMutation();
    const [editReportStudent] = useEditReportStudentMutation();
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);
    const [viewGroup] = useViewGroupMutation();

    const tabs = [
        { title: t('groups.attendance'),onClick: () => gotoAttendanceTab(groupId)  },
        ...(hasPlan ? [
            { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
        ] : []),
        ...(hasProgress ? [
            { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
        ] : []),
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []),
        { title: t('reports.title'),isActive: true},
        { title: t('documents'), onClick: () => gotoDocumentsTab(groupId)},
        { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
    ];

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    }

    const onSave = () => {
        if (formRef.current) {
            setSaving(true);
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        const body = { ...values};

        if(groupReportStudentId){
            editReportStudent({reportId, studentId, groupReportStudentId, body}).then((result) => {
                if (result?.error) {
                    console.log(result);
                    setFormErrors(result?.error?.data?.error?.violations);
                    toast.error(result.error.data.error.responseMessage);
                } else {
                    navigate("/groups/"+ groupId +"/student-reports/" + reportId);
                }
            });
        }else{
            addReportStudent({reportId, studentId, body}).then((result) => {
                if (result?.error) {
                    console.log(result);
                    setFormErrors(result?.error?.data?.error?.violations);
                    toast.error(result.error.data.error.responseMessage);
                } else {
                    navigate("/groups/"+ groupId +"/student-reports/" + reportId);
                }
            });
        }
        setSaving(false);
    };


    useEffect(() => {
        viewGroup(groupId).then((result) => {
            setHasTests(result?.data?.hasTests);
            setHasProgress(result?.data?.hasProgress);
            setHasPlan(result?.data?.hasPlan);
        });
        viewStudent(studentId).then((result) => {
            setStudentFullName(result?.data?.firstName + ' ' + result?.data?.lastName);
        });
        viewReportStudent({ reportId, studentId }).then((result) => {
            formRef.current.setValues({
                generalGrade: result?.data?.generalGrade ?? "",
            });            
            setGroupReportStudentId(result?.data?.id);
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={"/groups/"+ groupId +"/student-reports/" + reportId}/>
                <Button title={t('save')} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{studentFullName}</Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-user-group" />
                    <Link title={t("reports.title")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{ 
                        generalGrade: "", 
                    }}
                    onSubmit={(values,) => {
                        onSaved(values);
                    }}
                >
                    {({ values, handleChange, setFieldValue, resetForm }) => (
                        <Form>
                            <Row>
                                <Field isFullWidth={true}>
                                    <SummerNote
                                        required={true}
                                        saving={saving}
                                        height="500px"
                                        name="generalGrade"
                                        label={t("reports.generalGrade")}
                                        value={values?.generalGrade}
                                        onChange={handleChange}
                                        error={formErrors?.generalGrade}
                                    />
                                </Field> 
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default GeneralGrade;